// Canvly Admin Web — 앱 엔트리
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import AppRoutes from '@/routes'
import { Toaster } from '@/components/ui/sonner'
import PWAInstallBanner from '@/components/common/PWAInstallBanner'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 60초간 fresh — 메뉴 전환 시 캐시 즉시 표시 + 백그라운드 재검증 안 함
      staleTime: 60_000,
      // 5분간 캐시 유지 (메모리)
      gcTime: 5 * 60_000,
      retry: 1,
      // 탭 전환마다 재요청은 끔 (필요한 곳만 명시적으로)
      refetchOnWindowFocus: false,
      // 마운트 시 stale 인 경우만 재요청
      refetchOnMount: true,
      throwOnError: false,
    },
    mutations: {
      onError: (err: any) => {
        console.error('[Mutation Error]', err)
      },
    },
  },
})

function ErrorFallback({ error, resetError }: { error: any; resetError?: () => void }) {
  // 에러 종류별 구체적 메시지
  const errMsg = error?.message ?? ''
  let title = '오류가 발생했습니다'
  let description = '불편을 드려 죄송합니다.'
  let code = ''

  if (errMsg.includes('PGRST116') || errMsg.includes('not found') || errMsg.includes('No rows')) {
    title = '존재하지 않는 페이지입니다'
    description = '요청하신 데이터를 찾을 수 없습니다.'
  } else if (errMsg.includes('permission') || errMsg.includes('RLS') || errMsg.includes('row-level security')) {
    title = '접근 권한이 없습니다'
    description = '이 데이터에 접근할 수 있는 권한이 없습니다. 로그인 상태를 확인해주세요.'
  } else if (errMsg.includes('fetch') || errMsg.includes('network') || errMsg.includes('Failed to fetch')) {
    title = '연결에 실패했습니다'
    description = '인터넷 연결을 확인하고 다시 시도해주세요.'
  } else if (errMsg) {
    code = errMsg.slice(0, 100)
  }

  console.error('[ErrorBoundary]', error)

  return (
    <div className="min-h-screen bg-canvly-warm flex items-center justify-center">
      <div className="text-center max-w-md px-8">
        <h1 className="font-serif text-3xl text-canvly-charcoal mb-3">{title}</h1>
        <p className="text-canvly-stone text-sm mb-2">{description}</p>
        {code && <p className="text-canvly-mist text-xs mb-4 font-mono">{code}</p>}
        <div className="flex gap-3 justify-center mt-6">
          <button
            onClick={() => { window.history.back(); setTimeout(() => window.location.reload(), 100) }}
            className="px-5 py-2 border border-canvly-ash text-canvly-charcoal text-sm rounded-sm hover:border-canvly-gold transition-colors"
          >
            뒤로가기
          </button>
          <button
            onClick={() => {
              resetError?.()
              window.location.reload()
            }}
            className="px-5 py-2 bg-canvly-gold text-white text-sm rounded-sm"
          >
            새로고침
          </button>
        </div>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => <ErrorFallback error={error} resetError={resetError} />}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
          <Toaster />
          <PWAInstallBanner />
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  )
}
